import { reverse } from 'named-urls';

import routes from 'core/routes';
import { NotificationTarget } from 'core/constants';
import { NotificationType } from 'utils/api/modules/user/user.types';

export enum NotificationCategory {
  ALL = 'all',
  EVENTS = 'events',
  ACTIONS = 'actions',
  AUDITS = 'audits',
  SITES = 'sites',
  ASSETS = 'assets',
  DOCUMENTS = 'documents',
  RISKS = 'risks',
}

export const TARGET_TO_CATEGORY: Record<NotificationTarget, NotificationCategory> = {
  // Actions
  [NotificationTarget.NEW_TASK_ASSIGNED_TO_ME]: NotificationCategory.ACTIONS,
  [NotificationTarget.NEW_TASK_MESSAGE]: NotificationCategory.ACTIONS,
  [NotificationTarget.STATUS_UPDATE_OF_MY_TASK]: NotificationCategory.ACTIONS,
  [NotificationTarget.NEW_COMMENT_IN_MY_TASK]: NotificationCategory.ACTIONS,
  [NotificationTarget.ACKNOWLEDGEMENT_ASSIGNED_TO_ME]: NotificationCategory.ACTIONS,
  [NotificationTarget.TASK_DUE_TODAY]: NotificationCategory.ACTIONS,
  [NotificationTarget.ACKNOWLEDGEMENT_DUE_TODAY]: NotificationCategory.ACTIONS,
  // Events
  [NotificationTarget.NEW_EVENT_REPORTED]: NotificationCategory.EVENTS,
  [NotificationTarget.NEW_EVENT_ASSIGNED_TO_ME]: NotificationCategory.EVENTS,
  [NotificationTarget.NEW_EVENT_MESSAGE]: NotificationCategory.EVENTS,
  [NotificationTarget.EVENT_COLLABORATED]: NotificationCategory.EVENTS,
  [NotificationTarget.EVENT_COMMENT]: NotificationCategory.EVENTS,
  [NotificationTarget.EVENT_STAGE_CHANGED]: NotificationCategory.EVENTS,
  // Incidents → Events
  [NotificationTarget.NEW_INCIDENT_MESSAGE]: NotificationCategory.EVENTS,
  [NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE]: NotificationCategory.EVENTS,
  [NotificationTarget.NEW_INCIDENT_REPORTED]: NotificationCategory.EVENTS,
  [NotificationTarget.NEW_INCIDENT_ASSIGNED_TO_ME]: NotificationCategory.EVENTS,
  // Audits
  [NotificationTarget.NEW_FORM_SUBMITTED]: NotificationCategory.AUDITS,
  [NotificationTarget.AUDIT_COMMENT_MENTION]: NotificationCategory.AUDITS,
  [NotificationTarget.NEW_SCHEDULE_ASSIGNED_TO_ME]: NotificationCategory.AUDITS,
  [NotificationTarget.AUDIT_DUE_TODAY]: NotificationCategory.AUDITS,
  // Assets
  [NotificationTarget.NEW_ASSET_MESSAGE]: NotificationCategory.ASSETS,
  // Sites
  [NotificationTarget.NEW_SITE_MESSAGE]: NotificationCategory.SITES,
  [NotificationTarget.NEW_SITE_ASSIGNED_TO_ME]: NotificationCategory.SITES,
  // Documents
  [NotificationTarget.DOCUMENT_COMMENT_MENTION]: NotificationCategory.DOCUMENTS,
  // Risks
  [NotificationTarget.RISK_COMMENT_MENTION]: NotificationCategory.RISKS,
  [NotificationTarget.RISK_ACTION_ASSIGNED_TO_ME]: NotificationCategory.RISKS,
  [NotificationTarget.RISK_ACTION_DUE_TODAY]: NotificationCategory.RISKS,
};

export interface CategoryConfig {
  label: string;
  pluralLabel: string;
}

export const CATEGORY_CONFIG: Record<NotificationCategory, CategoryConfig> = {
  [NotificationCategory.ALL]: { label: 'All', pluralLabel: 'All' },
  [NotificationCategory.EVENTS]: { label: 'Event', pluralLabel: 'Events' },
  [NotificationCategory.ACTIONS]: { label: 'Action', pluralLabel: 'Actions' },
  [NotificationCategory.AUDITS]: { label: 'Audit', pluralLabel: 'Audits' },
  [NotificationCategory.SITES]: { label: 'Site', pluralLabel: 'Sites' },
  [NotificationCategory.ASSETS]: { label: 'Asset', pluralLabel: 'Assets' },
  [NotificationCategory.DOCUMENTS]: { label: 'Document', pluralLabel: 'Documents' },
  [NotificationCategory.RISKS]: { label: 'Risk', pluralLabel: 'Risks' },
};

export function getCategoryForNotification(target: NotificationTarget): NotificationCategory | undefined {
  return TARGET_TO_CATEGORY[target];
}

export function getTargetsForCategory(category: NotificationCategory): NotificationTarget[] {
  return (Object.keys(TARGET_TO_CATEGORY) as NotificationTarget[]).filter(
    (target) => TARGET_TO_CATEGORY[target] === category
  );
}

// A due-today audit has no per-instance route the resolver can rebuild from one uuid, so the
// backend ships the full deep link. Use its path so it routes in-app across environments.
const pathFromUrl = (url?: string): string => {
  if (!url) return '';
  try {
    const { pathname, search } = new URL(url);
    return pathname + search;
  } catch {
    return url;
  }
};

export function getLinkForNotification(item: NotificationType): string {
  switch (item.target) {
    case NotificationTarget.NEW_INCIDENT_MESSAGE:
    case NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE:
    case NotificationTarget.NEW_INCIDENT_REPORTED:
    case NotificationTarget.NEW_INCIDENT_ASSIGNED_TO_ME:
      return reverse(routes.dashboard.events.details, { entityId: item.instance.uuid });
    case NotificationTarget.NEW_TASK_MESSAGE:
    case NotificationTarget.NEW_TASK_ASSIGNED_TO_ME:
    case NotificationTarget.STATUS_UPDATE_OF_MY_TASK:
    case NotificationTarget.NEW_COMMENT_IN_MY_TASK:
    case NotificationTarget.TASK_DUE_TODAY:
      return reverse(routes.dashboard.tasks.details, { taskId: item.instance.uuid });
    case NotificationTarget.NEW_FORM_SUBMITTED:
      return reverse(routes.dashboard.audits.forms.details, { formId: item.instance.uuid });
    case NotificationTarget.NEW_SCHEDULE_ASSIGNED_TO_ME:
      return routes.dashboard.tasks.audits.toString();
    case NotificationTarget.AUDIT_DUE_TODAY:
      return pathFromUrl(item.notificationUrl);
    case NotificationTarget.AUDIT_COMMENT_MENTION:
      return (
        pathFromUrl(item.notificationUrl) ||
        reverse(routes.dashboard.audits.forms.details, { formId: item.instance.uuid })
      );
    case NotificationTarget.ACKNOWLEDGEMENT_ASSIGNED_TO_ME:
    case NotificationTarget.ACKNOWLEDGEMENT_DUE_TODAY:
      return reverse(routes.dashboard.acknowledgements.details, { acknowledgementId: item.instance.uuid });
    case NotificationTarget.NEW_EVENT_REPORTED:
    case NotificationTarget.NEW_EVENT_ASSIGNED_TO_ME:
    case NotificationTarget.NEW_EVENT_MESSAGE:
    case NotificationTarget.EVENT_COLLABORATED:
    case NotificationTarget.EVENT_COMMENT:
    case NotificationTarget.EVENT_STAGE_CHANGED:
      return reverse(routes.dashboard.events.details, { entityId: item.instance.uuid });
    case NotificationTarget.NEW_ASSET_MESSAGE:
      return reverse(routes.dashboard.assets.details, { entityId: item.instance.uuid });
    case NotificationTarget.NEW_SITE_MESSAGE:
    case NotificationTarget.NEW_SITE_ASSIGNED_TO_ME:
      return reverse(routes.dashboard.sites.details, { entityId: item.instance.uuid });
    case NotificationTarget.DOCUMENT_COMMENT_MENTION:
      return reverse(routes.dashboard.documents.details, { entityId: item.instance.uuid });
    case NotificationTarget.RISK_COMMENT_MENTION:
      return reverse(routes.dashboard.risks.details, { entityId: item.instance.uuid });
    case NotificationTarget.RISK_ACTION_ASSIGNED_TO_ME:
    case NotificationTarget.RISK_ACTION_DUE_TODAY:
      return reverse(routes.dashboard.tasks.riskAction, { actionId: item.instance.uuid });
    default:
      return '';
  }
}

const COMMENT_TARGETS: Set<NotificationTarget> = new Set([
  NotificationTarget.NEW_INCIDENT_MESSAGE,
  NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE,
  NotificationTarget.NEW_TASK_MESSAGE,
  NotificationTarget.NEW_EVENT_MESSAGE,
  NotificationTarget.NEW_ASSET_MESSAGE,
  NotificationTarget.NEW_SITE_MESSAGE,
  NotificationTarget.EVENT_COMMENT,
  NotificationTarget.NEW_COMMENT_IN_MY_TASK,
  NotificationTarget.DOCUMENT_COMMENT_MENTION,
  NotificationTarget.AUDIT_COMMENT_MENTION,
  NotificationTarget.RISK_COMMENT_MENTION,
]);

export function isCommentNotification(target: NotificationTarget): boolean {
  return COMMENT_TARGETS.has(target);
}

// Due-today reminders are system-generated (no actor) and share one inbox treatment (clock icon,
// "… is due today" copy) across actions, acknowledgements and audits.
const DUE_TODAY_TARGETS: Set<NotificationTarget> = new Set([
  NotificationTarget.TASK_DUE_TODAY,
  NotificationTarget.AUDIT_DUE_TODAY,
  NotificationTarget.ACKNOWLEDGEMENT_DUE_TODAY,
  NotificationTarget.RISK_ACTION_DUE_TODAY,
]);

export function isDueTodayNotification(target: NotificationTarget): boolean {
  return DUE_TODAY_TARGETS.has(target);
}

export const CATEGORY_LIST = [
  NotificationCategory.ALL,
  NotificationCategory.EVENTS,
  NotificationCategory.ACTIONS,
  NotificationCategory.AUDITS,
  NotificationCategory.SITES,
  NotificationCategory.ASSETS,
  NotificationCategory.DOCUMENTS,
  NotificationCategory.RISKS,
];
